import { useEffect, useMemo, useState } from "react";
import {
    Download,
    RefreshCw,
    Store,
    CheckCircle2,
    Clock3,
    XCircle,
} from "lucide-react";

import { getProviders } from "../services/adminApi";

import ProviderFilters from "../components/Provider/ProviderFilters";
import ProviderTable from "../components/Provider/ProviderTable";

import "./Providers.css";

function Providers() {

    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        search: "",
        status: "",
        type: "",
        city: "",
        sort: "latest",
    });

    const loadProviders = async () => {

        try {

            setLoading(true);

            const res = await getProviders();

            setProviders(res.data.providers || []);

        } catch (error) {

            console.error(error);

        } finally {

            setLoading(false);

        }
    };

    useEffect(() => {

        loadProviders();

    }, []);

    const handleFilterChange = (key, value) => {

        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));

    };

    const filteredProviders = useMemo(() => {

        let data = [...providers];

        if (filters.search) {

            const search = filters.search.toLowerCase();

            data = data.filter((provider) => {

                const business =
                    provider.business?.businessName?.toLowerCase() || "";

                const owner =
                    provider.ownerName?.toLowerCase() || "";

                const email =
                    provider.email?.toLowerCase() || "";

                const phone =
                    provider.phone?.toLowerCase() || "";

                return (
                    business.includes(search) ||
                    owner.includes(search) ||
                    email.includes(search) ||
                    phone.includes(search)
                );
            });
        }

        if (filters.status) {

            data = data.filter(
                (provider) =>
                    provider.status?.toLowerCase() ===
                    filters.status.toLowerCase()
            );

        }

        if (filters.type) {

            data = data.filter(
                (provider) =>
                    provider.providerType === filters.type
            );

        }

        if (filters.city) {

            data = data.filter((provider) =>
                provider.address?.city
                    ?.toLowerCase()
                    .includes(filters.city.toLowerCase())
            );

        }

        switch (filters.sort) {

            case "oldest":
                data.sort(
                    (a, b) =>
                        new Date(a.createdAt) -
                        new Date(b.createdAt)
                );
                break;

            case "az":
                data.sort((a, b) =>
                    (a.business?.businessName || "").localeCompare(
                        b.business?.businessName || ""
                    )
                );
                break;

            case "za":
                data.sort((a, b) =>
                    (b.business?.businessName || "").localeCompare(
                        a.business?.businessName || ""
                    )
                );
                break;

            default:
                data.sort(
                    (a, b) =>
                        new Date(b.createdAt) -
                        new Date(a.createdAt)
                );
        }

        return data;

    }, [providers, filters]);

    const totalProviders = providers.length;

    const approvedProviders = providers.filter(
        (provider) =>
            provider.status?.toLowerCase() === "approved"
    ).length;

    const pendingProviders = providers.filter(
        (provider) =>
            provider.status?.toLowerCase() === "pending"
    ).length;

    const rejectedProviders = providers.filter(
        (provider) =>
            provider.status?.toLowerCase() === "rejected"
    ).length;

    return (

        <div className="providers-page">

            <div className="providers-header">

                <div>

                    <h1>Provider Management</h1>

                    <p>
                        Manage all registered providers.
                    </p>

                </div>

                <div className="providers-actions">

                    <button className="export-btn">

                        <Download size={18} />

                        Export

                    </button>

                    <button
                        className="refresh-btn"
                        onClick={loadProviders}
                    >

                        <RefreshCw size={18} />

                        Refresh

                    </button>

                </div>

            </div>

            <div className="provider-overview">

                <div className="overview-card">

                    <div className="overview-icon total">

                        <Store size={24} />

                    </div>

                    <div>

                        <h2>{totalProviders}</h2>

                        <p>Total Providers</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon approved">

                        <CheckCircle2 size={24} />

                    </div>

                    <div>

                        <h2>{approvedProviders}</h2>

                        <p>Approved</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon pending">

                        <Clock3 size={24} />

                    </div>

                    <div>

                        <h2>{pendingProviders}</h2>

                        <p>Pending</p>

                    </div>

                </div>

                <div className="overview-card">

                    <div className="overview-icon rejected">

                        <XCircle size={24} />

                    </div>

                    <div>

                        <h2>{rejectedProviders}</h2>

                        <p>Rejected</p>

                    </div>

                </div>

            </div>

            <ProviderFilters
                filters={filters}
                onChange={handleFilterChange}
            />

            <div className="providers-section">

                <ProviderTable
                    providers={filteredProviders}
                    loading={loading}
                    reload={loadProviders}
                />

            </div>

        </div>
    );
}

export default Providers;